// Drives the browser side of the WebAuthn ceremony for both passkey registration (account
// management) and passkey sign-in (login). All configuration is read from data-* attributes so the
// markup stays inline-script free for the Content-Security-Policy. The server returns ready-made
// option JSON; this script only runs navigator.credentials and posts the result back through the
// owning form, which carries its own antiforgery token.
(() => {
    "use strict";

    // Require the full JSON-based WebAuthn API (parse*FromJSON + toJSON). Some browser versions ship
    // navigator.credentials but not these helpers; without toJSON, serialization would silently yield an
    // empty credential, so such browsers are treated as unsupported and the passkey UI is hidden.
    const supported = typeof window.PublicKeyCredential !== "undefined"
        && !!(navigator.credentials && navigator.credentials.create && navigator.credentials.get)
        && typeof PublicKeyCredential.parseCreationOptionsFromJSON === "function"
        && typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function"
        && typeof PublicKeyCredential.prototype.toJSON === "function";

    // Tracks the in-flight conditional-UI (autofill) request so an explicit button click can cancel it.
    let conditionalController = null;

    function hide(element) {
        if (element) {
            element.hidden = true;
        }
    }

    function setField(form, name, value) {
        const field = form && form.querySelector(`[name="${name}"]`);
        if (field) {
            field.value = value;
        }
    }

    function isCancellation(error) {
        return error && (error.name === "NotAllowedError" || error.name === "AbortError");
    }

    function serialize(credential) {
        const json = credential.toJSON();
        // Some authenticators — notably password-manager browser extensions such as 1Password — return a
        // toJSON() payload without clientExtensionResults, which the server requires and rejects the
        // credential without. Backfill it from the live credential so attestation/assertion succeeds.
        json.clientExtensionResults ??= credential.getClientExtensionResults?.() ?? {};
        return JSON.stringify(json);
    }

    function optionHeaders(trigger) {
        const headerName = trigger.getAttribute("data-antiforgery-header");
        const token = trigger.getAttribute("data-antiforgery-token");
        const headers = {};
        if (headerName && token) {
            headers[headerName] = token;
        }
        return headers;
    }

    async function fetchOptions(url, trigger) {
        const response = await fetch(url, {
            method: "POST",
            headers: optionHeaders(trigger),
            credentials: "same-origin"
        });
        if (!response.ok) {
            throw new Error(`Passkey options request failed (${response.status}).`);
        }
        return response.json();
    }

    function form(trigger) {
        return document.getElementById(trigger.getAttribute("data-passkey-form"));
    }

    function failForm(trigger, message) {
        const owner = form(trigger);
        setField(owner, trigger.getAttribute("data-passkey-error-field"), message);
        if (owner) {
            owner.submit();
        }
    }

    // Runs a WebAuthn ceremony, swallowing user cancellation and surfacing any other failure on the form.
    // The trigger is disabled for the duration so a second click cannot start a concurrent ceremony
    // (browsers reject more than one outstanding navigator.credentials call); it is re-enabled only when
    // the ceremony does not submit the form.
    async function runCeremony(trigger, ceremony, failMessage) {
        trigger.disabled = true;
        try {
            await ceremony();
        } catch (error) {
            trigger.disabled = false;
            if (isCancellation(error)) {
                return;
            }
            failForm(trigger, failMessage);
        }
    }

    async function createCredential(trigger) {
        const optionsJson = await fetchOptions(trigger.getAttribute("data-passkey-options-url"), trigger);
        const options = PublicKeyCredential.parseCreationOptionsFromJSON(optionsJson);
        const credential = await navigator.credentials.create({ publicKey: options });
        const owner = form(trigger);
        setField(owner, trigger.getAttribute("data-passkey-credential-field"), serialize(credential));
        owner.submit();
    }

    function createPasskey(trigger) {
        return runCeremony(trigger, () => createCredential(trigger),
            "The passkey could not be created. Please try again.");
    }

    async function requestAssertion(trigger, mediation, signal) {
        let url = trigger.getAttribute("data-passkey-options-url");
        const usernameFieldId = trigger.getAttribute("data-passkey-username-field");
        if (usernameFieldId) {
            const input = document.getElementById(usernameFieldId);
            const username = input && input.value ? input.value.trim() : "";
            url += `?username=${encodeURIComponent(username)}`;
        }

        const optionsJson = await fetchOptions(url, trigger);
        const options = PublicKeyCredential.parseRequestOptionsFromJSON(optionsJson);
        const credential = await navigator.credentials.get({ publicKey: options, mediation, signal });

        const owner = form(trigger);
        setField(owner, trigger.getAttribute("data-passkey-credential-field"), serialize(credential));
        owner.submit();
    }

    function signInWithPasskey(trigger) {
        if (conditionalController) {
            conditionalController.abort();
            conditionalController = null;
        }
        return runCeremony(trigger, () => requestAssertion(trigger, undefined, undefined),
            "Passkey sign-in could not be completed. Please try again or use your password.");
    }

    async function startConditionalUi(trigger) {
        if (typeof PublicKeyCredential.isConditionalMediationAvailable !== "function") {
            return;
        }
        try {
            if (!(await PublicKeyCredential.isConditionalMediationAvailable())) {
                return;
            }
            conditionalController = new AbortController();
            await requestAssertion(trigger, "conditional", conditionalController.signal);
        } catch {
            // Autofill is best-effort: an aborted or unfulfilled conditional request is expected and ignored.
        }
    }

    document.addEventListener("DOMContentLoaded", () => {
        const createButtons = Array.from(document.querySelectorAll("[data-passkey-create]"));
        const signInButtons = Array.from(document.querySelectorAll("[data-passkey-signin]"));

        if (!supported) {
            createButtons.forEach(hide);
            signInButtons.forEach(hide);
            document.querySelectorAll("[data-passkey-requires-support]").forEach(hide);
            return;
        }

        createButtons.forEach(button => button.addEventListener("click", () => createPasskey(button)));
        signInButtons.forEach(button => button.addEventListener("click", () => signInWithPasskey(button)));

        // Only one conditional-UI (autofill) request may be outstanding per page, so start it once.
        if (signInButtons.length > 0) {
            startConditionalUi(signInButtons[0]);
        }
    });
})();
